import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

@observer
class ConventionPaymentSummary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            data: null,
            users: [],
            expandedUsers: {},
            sortBy: 'name',
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) {
            this.setState({ loading: false, error: 'No convention loaded' });
            return;
        }
        try {
            const response = await util.fetchApi(`/api/conventions/${convention.id}/payment-totals`, { method: 'GET' });
            const data = response?.data;
            const users = response?.included?.users || [];
            this.setState({ loading: false, data, users });
        } catch (err) {
            this.setState({ loading: false, error: err.message || 'Failed to load' });
        }
    }

    toggleUser = (userId) => {
        this.setState(prev => ({
            expandedUsers: { ...prev.expandedUsers, [userId]: !prev.expandedUsers[userId] },
        }));
    }

    setSortBy = (sortBy) => {
        this.setState({ sortBy });
    }

    formatAmount = (amount) => `${util.format(amount || 0, 0)} kr`;

    findUser = (userId) => this.state.users.find(u => u.id === userId);

    paymentMethodLabel = (method) => {
        if (!method) return '—';
        const map = {
            vipps: 'Vipps',
            paypal: 'PayPal',
            cash: 'Kontant',
            card: 'Kort',
            cardApp: 'Kort (App)',
            cardExternal: 'Kort (ekstern)',
            invoice: 'Faktura',
            bank: 'Bank',
            account: 'Bank',
            giftCard: 'Gavekort',
        };
        return map[method] || method;
    }

    render() {
        const { loading, error, data, users, expandedUsers, sortBy } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (loading) {
            return (
                <div class='text-center py-5'>
                    <i class='fa-solid fa-spinner fa-spin fa-2x' />
                </div>
            );
        }

        if (error) {
            return (
                <div class='alert alert-danger m-3'>
                    {error}
                </div>
            );
        }

        if (!data) {
            return (
                <div class='alert alert-info m-3'>
                    <Text id='convention.payment-no-data'>No payment data</Text>
                </div>
            );
        }

        const { conventionPaidTotal, conventionPaymentsCount, payments = [], userTotals = [] } = data;

        const sortedTotals = [...userTotals].sort((a, b) => {
            if (sortBy === 'conventionPaid') return (b.conventionPaid || 0) - (a.conventionPaid || 0);
            if (sortBy === 'totalPaid') return (b.totalPaid || 0) - (a.totalPaid || 0);
            const ua = this.findUser(a.userId);
            const ub = this.findUser(b.userId);
            const an = `${ua?.lastname || ''} ${ua?.firstname || ''}`.trim().toLowerCase();
            const bn = `${ub?.lastname || ''} ${ub?.firstname || ''}`.trim().toLowerCase();
            return an.localeCompare(bn);
        });

        const paymentsByUser = {};
        payments.forEach(p => {
            const key = p.user || p.conventionParticipantId || 0;
            if (!paymentsByUser[key]) paymentsByUser[key] = [];
            paymentsByUser[key].push(p);
        });

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                <span>
                    <i class='fa-duotone fa-cash-register me-2' />
                    <Text id='convention.payment-summary'>Payment summary</Text>
                </span>
            </div>

            <div class={`mt-0 mb-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='row text-center'>
                    <div class='col-6'>
                        <div class='text-body-secondary small'>
                            <Text id='convention.payment-total-paid'>Total paid this convention</Text>
                        </div>
                        <div class='fs-3 fw-bold text-success'>
                            {this.formatAmount(conventionPaidTotal)}
                        </div>
                    </div>
                    <div class='col-6'>
                        <div class='text-body-secondary small'>
                            <Text id='convention.payment-count'>Payments</Text>
                        </div>
                        <div class='fs-3 fw-bold'>
                            {conventionPaymentsCount || 0}
                        </div>
                    </div>
                </div>
            </div>

            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                <span>
                    <i class='fa-duotone fa-users me-2' />
                    <Text id='convention.payment-per-user'>Per user</Text>
                </span>
                <span class='btn-group btn-group-sm'>
                    <button
                        type='button'
                        class={`btn btn-sm ${sortBy === 'name' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => this.setSortBy('name')}
                    >
                        <Text id='convention.sort-name'>Name</Text>
                    </button>
                    <button
                        type='button'
                        class={`btn btn-sm ${sortBy === 'conventionPaid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => this.setSortBy('conventionPaid')}
                    >
                        <Text id='convention.sort-conv-paid'>Conv. paid</Text>
                    </button>
                    <button
                        type='button'
                        class={`btn btn-sm ${sortBy === 'totalPaid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => this.setSortBy('totalPaid')}
                    >
                        <Text id='convention.sort-total-paid'>All-time</Text>
                    </button>
                </span>
            </div>

            <div class={`mt-0 mb-3 mx-3 px-0 py-0 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='list-group list-group-flush'>
                    {sortedTotals.length === 0 && (
                        <div class='p-3 text-body-secondary text-center'>
                            <Text id='convention.payment-no-users'>No participants with payments</Text>
                        </div>
                    )}
                    {sortedTotals.map(row => {
                        const u = this.findUser(row.userId);
                        const isOpen = !!expandedUsers[row.userId];
                        const userPayments = paymentsByUser[row.userId] || [];
                        return (
                            <div class='list-group-item' key={`u-${row.userId}`}>
                                <div
                                    class='d-flex flex-row justify-content-between align-items-center'
                                    onClick={() => this.toggleUser(row.userId)}
                                    style='cursor: pointer;'
                                >
                                    <div class='d-flex flex-column'>
                                        <span class='fw-bold'>
                                            <i class={`fa-solid me-2 ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                                            {u ? `${u.firstname || ''} ${u.lastname || ''}`.trim() : `User #${row.userId}`}
                                        </span>
                                        {u?.cellphone && (
                                            <small class='text-body-secondary ms-4'>{u.cellphone}</small>
                                        )}
                                    </div>
                                    <div class='d-flex flex-column align-items-end'>
                                        <span>
                                            <small class='text-body-secondary me-1'>
                                                <Text id='convention.payment-conv-short'>conv.</Text>
                                            </small>
                                            <span class='badge text-bg-success'>
                                                {this.formatAmount(row.conventionPaid)}
                                            </span>
                                        </span>
                                        <small class='text-body-secondary mt-1'>
                                            <Text id='convention.payment-total-short'>all-time</Text>
                                            <span class='ms-1'>{this.formatAmount(row.totalPaid)}</span>
                                            {row.totalCount > 0 && (
                                                <span class='ms-1'>({row.totalCount})</span>
                                            )}
                                        </small>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div class='mt-2 ps-4'>
                                        {userPayments.length === 0 && (
                                            <small class='text-body-secondary'>
                                                <Text id='convention.payment-none-for-conv'>No payments for this convention</Text>
                                            </small>
                                        )}
                                        {userPayments.map(p => (
                                            <div class='d-flex flex-row justify-content-between border-top py-1' key={`p-${p.id}`}>
                                                <div class='d-flex flex-column'>
                                                    <span>
                                                        <i class='fa-regular fa-receipt me-1 text-body-secondary' />
                                                        {p.title || p.type || <Text id='convention.payment-untitled'>Payment</Text>}
                                                    </span>
                                                    <small class='text-body-secondary'>
                                                        {p.paidDate ? util.isoDate(p.paidDate, false, false, true) : '—'}
                                                        <span class='ms-2'>{this.paymentMethodLabel(p.paymentMethod)}</span>
                                                    </small>
                                                </div>
                                                <span class='fw-bold align-self-center'>
                                                    {this.formatAmount(p.amount)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>);
    }
}

export default ConventionPaymentSummary;
